/**
 * AudioCache Service
 * 
 * Handles loading, caching, and playing audio files throughout the application
 * Supports volume control, muting, and tracking purchased sounds
 */

interface AudioCacheOptions {
  defaultVolume?: number;
  enableLogging?: boolean;
}

export class AudioCache {
  private cache: Map<string, HTMLAudioElement>;
  private globalVolume: number;
  private muted: boolean;
  private purchasedSounds: Set<string>;
  private activeSounds: Set<string>;
  private eventSoundMap: Map<string, string>; // Maps event keys to sound keys
  private enableLogging: boolean;
  
  // Public getters and methods for volume control
  public get isMuted(): boolean {
    return this.muted;
  }
  
  public getGlobalVolume(): number {
    return this.globalVolume;
  }

  constructor(options: AudioCacheOptions = {}) {
    this.cache = new Map();
    
    // Load volume and mute settings from localStorage if available
    const savedVolume = localStorage.getItem('gameVolume');
    const savedMuted = localStorage.getItem('gameMuted');
    
    this.globalVolume = savedVolume !== null ? parseFloat(savedVolume) : (options.defaultVolume ?? 0.5);
    this.muted = savedMuted !== null ? savedMuted === 'true' : false;
    
    this.purchasedSounds = new Set();
    this.activeSounds = new Set();
    this.eventSoundMap = new Map();
    this.enableLogging = options.enableLogging ?? false;
    
    this.log(`AudioCache initialized with volume: ${this.globalVolume}, muted: ${this.muted}`);
  }

  /**
   * Logs messages if logging is enabled
   */
  private log(message: string, ...args: any[]): void {
    if (this.enableLogging) {
      // console.log(`[AudioCache] ${message}`, ...args);
    }
  }

  /**
   * Preloads an audio file and caches it for future use
   */
  public async preloadAudio(key: string, url: string): Promise<void> {
    this.log(`Preloading audio: ${key} from ${url}`);
    
    if (this.cache.has(key)) {
      this.log(`Audio ${key} already cached`);
      return;
    }

    try {
      const audio = new Audio();
      
      // Set initial volume
      audio.volume = this.muted ? 0 : this.globalVolume;
      
      // IMPORTANT FIX: Ensure we don't use file:// protocol by resolving paths
      // If URL doesn't start with http/https, ensure it's a proper web path
      let resolvedUrl = url;
      if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('data:')) {
        // Ensure it starts with a forward slash for absolute web path
        resolvedUrl = url.startsWith('/') ? url : `/${url}`;
        
        // Double check we don't have file:// protocol
        if (resolvedUrl.startsWith('file://')) {
          resolvedUrl = resolvedUrl.replace('file://', '/');
        }
        
        this.log(`Resolved audio URL from ${url} to ${resolvedUrl}`);
      }
      
      // Create a promise that resolves when the audio is loaded or times out
      const loadPromise = new Promise<void>((resolve) => {
        // Set up load success handler
        audio.addEventListener('canplaythrough', () => {
          this.log(`Audio ${key} loaded successfully`);
          resolve();
        }, { once: true });
        
        // Set up error handler
        audio.addEventListener('error', (e) => {
          this.log(`Warning: Failed to load audio ${key}: ${e.type}`, e);
          // Don't reject, just resolve anyway
          // This ensures one bad audio doesn't break the entire app
          resolve();
        }, { once: true });
        
        // Set a timeout in case the audio takes too long to load
        const timeoutId = setTimeout(() => {
          this.log(`Audio ${key} load timed out, continuing anyway`);
          resolve();
        }, 3000); // 3 second timeout
        
        // Clean up timeout if load completes
        audio.addEventListener('canplaythrough', () => clearTimeout(timeoutId), { once: true });
        audio.addEventListener('error', () => clearTimeout(timeoutId), { once: true });
        
        // Set source after adding event listeners
        audio.src = resolvedUrl;
        
        // Start loading
        audio.load();
      });
      
      // Wait for the load to complete or timeout
      await loadPromise;
      
      // Cache the audio even if it failed to load
      // This prevents repeated failed load attempts
      this.cache.set(key, audio);
      this.log(`Audio ${key} cached`);
    } catch (error) {
      // Log the error but don't throw
      this.log(`Error preloading audio ${key}, using silent fallback:`, error);
      
      // Create a silent audio element as a fallback
      const fallbackAudio = new Audio();
      fallbackAudio.volume = 0;
      this.cache.set(key, fallbackAudio);
    }
  }

  /**
   * Plays a cached audio file
   */
  /**
   * Check if user has interacted with the document
   * This helps prevent browser errors when trying to play audio before interaction
   */
  private hasUserInteracted(): boolean {
    return document.documentElement.hasAttribute('data-user-interacted');
  }

  /**
   * Mark that the user has interacted with the document
   * Call this when handling user input events like clicks
   */
  public markUserInteraction(): void {
    if (!this.hasUserInteracted()) {
      document.documentElement.setAttribute('data-user-interacted', 'true');
      this.log('User interaction detected');
    }
  }

  /**
   * Plays a cached audio file
   * Returns a Promise that resolves when playing succeeds or fails
   */
  public play(key: string): Promise<void> {
    const audio = this.cache.get(key);
    
    if (!audio) {
      this.log(`Audio ${key} not found in cache, attempting to create a silent fallback`);
      // Create a silent audio fallback
      const fallbackAudio = new Audio();
      fallbackAudio.volume = 0;
      this.cache.set(key, fallbackAudio);
      return Promise.resolve(); // Silent return instead of throwing
    }

    // Check if user has interacted with the document
    if (!this.hasUserInteracted()) {
      this.log(`User interaction required before playing audio ${key}`);
      return Promise.resolve(); // Return without playing
    }

    try {
      // Update volume to current settings
      audio.volume = this.muted ? 0 : this.globalVolume;
      
      // Reset audio to beginning if it's already playing
      audio.currentTime = 0;
      
      // Play the audio and handle errors properly
      return audio.play()
        .catch(error => {
          this.log(`Warning: Error playing audio ${key}:`, error);
          // Don't throw errors from here - just log them
        })
        .then(() => {
          this.log(`Playing audio ${key}`);
        });
    } catch (error) {
      // Just log the error, don't throw
      this.log(`Warning: Error playing audio ${key}:`, error);
      return Promise.resolve();
    }
  }

  /**
   * Sets the global volume for all audio
   */
  public setGlobalVolume(volume: number): void {
    if (volume < 0 || volume > 1) {
      throw new Error(`Volume must be between 0 and 1, got ${volume}`);
    }
    
    this.globalVolume = volume;
    this.log(`Global volume set to ${volume}`);
    
    // Save to localStorage
    localStorage.setItem('gameVolume', volume.toString());
    
    // Update volume for all cached audio
    this.cache.forEach(audio => {
      audio.volume = this.muted ? 0 : this.globalVolume;
    });
  }

  /**
   * Mutes or unmutes all audio
   */
  public setMuted(muted: boolean): void {
    this.muted = muted;
    this.log(`Audio ${muted ? 'muted' : 'unmuted'}`);
    
    // Save to localStorage
    localStorage.setItem('gameMuted', muted.toString());
    
    // Update volume for all cached audio
    this.cache.forEach(audio => {
      audio.volume = muted ? 0 : this.globalVolume;
    });
  }

  /**
   * Adds a purchased sound to the user's collection
   */
  public addPurchasedSound(soundKey: string): void {
    this.purchasedSounds.add(soundKey);
    this.log(`Added purchased sound: ${soundKey}`);
  }

  /**
   * Checks if a sound has been purchased
   */
  public hasPurchasedSound(soundKey: string): boolean {
    return this.purchasedSounds.has(soundKey);
  }

  /**
   * Gets all purchased sounds
   */
  public getPurchasedSounds(): string[] {
    return Array.from(this.purchasedSounds);
  }

  /**
   * Activates a purchased sound for use in the game
   */
  public activateSound(soundKey: string): void {
    if (!this.purchasedSounds.has(soundKey)) {
      throw new Error(`Cannot activate sound ${soundKey} as it has not been purchased`);
    }
    
    this.activeSounds.add(soundKey);
    this.log(`Activated sound: ${soundKey}`);
  }

  /**
   * Deactivates a sound
   */
  public deactivateSound(soundKey: string): void {
    this.activeSounds.delete(soundKey);
    this.log(`Deactivated sound: ${soundKey}`);
  }

  /**
   * Checks if a sound is activated
   */
  public isSoundActive(soundKey: string): boolean {
    return this.activeSounds.has(soundKey);
  }

  /**
   * Gets all active sounds
   */
  public getActiveSounds(): string[] {
    return Array.from(this.activeSounds);
  }

  /**
   * Updates purchased sounds from an API response
   */
  public updatePurchasedSounds(sounds: string[]): void {
    this.purchasedSounds = new Set(sounds);
    this.log(`Updated purchased sounds: ${sounds.length} sounds`);
  }

  /**
   * Updates active sounds from an API response or local storage
   */
  public updateActiveSounds(sounds: string[]): void {
    this.activeSounds = new Set(sounds);
    this.log(`Updated active sounds: ${sounds.length} sounds`);
  }

  /**
   * Clears the audio cache
   * @param fullReset If true, also clears sound mappings, purchased sounds, and active sounds
   */
  public reset(fullReset: boolean = false): void {
    // Stop and remove all cached audio
    this.cache.forEach(audio => {
      audio.pause();
      audio.src = '';
    });
    
    this.cache.clear();
    
    // Optionally clear all other state
    if (fullReset) {
      this.purchasedSounds.clear();
      this.activeSounds.clear();
      this.eventSoundMap.clear();
      this.log('Full reset: Audio cache, purchased sounds, active sounds, and mappings cleared');
    } else {
      this.log('Audio cache cleared (mappings and sound sets preserved)');
    }
  }

  /**
   * Maps a game event to a specific sound
   * This allows precise control over which sound plays for which event
   * 
   * @param eventKey The event key (e.g., 'gameplay.cardPlace')
   * @param soundKey The sound key to play for this event
   */
  public mapSoundToEvent(eventKey: string, soundKey: string): void {
    this.eventSoundMap.set(eventKey, soundKey);
    this.log(`Mapped event ${eventKey} to sound ${soundKey}`);
  }
  
  /**
   * Removes a mapping for a game event
   * 
   * @param eventKey The event key to unmap
   */
  public unmapSoundFromEvent(eventKey: string): void {
    if (this.eventSoundMap.has(eventKey)) {
      this.eventSoundMap.delete(eventKey);
      this.log(`Unmapped event ${eventKey}`);
    }
  }
  
  /**
   * Gets the sound key mapped to a specific event
   * 
   * @param eventKey The event key to look up
   * @returns The mapped sound key, or undefined if no mapping exists
   */
  public getSoundForEvent(eventKey: string): string | undefined {
    return this.eventSoundMap.get(eventKey);
  }
  
  /**
   * Clears all event-to-sound mappings
   */
  public clearSoundMappings(): void {
    this.eventSoundMap.clear();
    this.log('Cleared all sound mappings');
  }

  /**
   * Checks if a sound is cached
   * 
   * @param key The key to check
   * @returns True if the sound is cached, false otherwise
   */
  public isCached(key: string): boolean {
    return this.cache.has(key);
  }

  /**
   * Gets the appropriate sound URL for a sound type
   * If the user has an active custom sound for this type, it returns that
   * Otherwise, it falls back to the default sound
   * 
   * @param soundKey The key for the sound type (e.g., 'gameplay.cardPlace')
   * @param defaultUrl The URL to the default sound if no custom sound is active
   * @returns The URL to use for this sound
   */
  public getSoundUrl(soundKey: string, defaultUrl: string): string {
    // First, check if there's a direct mapping for this event
    const mappedSoundKey = this.eventSoundMap.get(soundKey);
    if (mappedSoundKey && this.activeSounds.has(mappedSoundKey)) {
      // Get the cached audio for this mapped sound
      const mappedAudio = this.cache.get(mappedSoundKey);
      if (mappedAudio) {
        this.log(`Using mapped sound ${mappedSoundKey} for event ${soundKey}`);
        return mappedAudio.src;
      }
    }
    
    // If no direct mapping, check for active sounds that match the type
    const customSoundIds = Array.from(this.activeSounds).filter(id => 
      id.includes(soundKey) || // Direct match
      // Category match (e.g., if the sound key is 'gameplay.cardPlace', 
      // a sound with ID 'custom-gameplay' might be a match)
      (soundKey.includes('.') && id.includes(soundKey.split('.')[0]))
    );
    
    if (customSoundIds.length > 0) {
      // If there are multiple active sounds for this type, pick one at random
      const randomIndex = Math.floor(Math.random() * customSoundIds.length);
      const customSoundId = customSoundIds[randomIndex];
      
      // Get the cached audio for this custom sound
      const customAudio = this.cache.get(customSoundId);
      if (customAudio) {
        this.log(`Using custom sound ${customSoundId} for ${soundKey}`);
        return customAudio.src;
      }
    }
    
    // Fall back to default sound
    this.log(`Using default sound for ${soundKey}`);
    return defaultUrl;
  }
}

// Create a singleton instance for global use
export const audioCache = new AudioCache({ enableLogging: false });

// Export default audio cache for easy imports
export default audioCache;